import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react'
import { useNavigate } from 'react-router-dom'
import type { NavigateOptions } from 'react-router-dom'

type FogPhase = 'hidden' | 'closing' | 'closed' | 'opening'

interface FogTransitionCtx {
  /** Like land “World map” / MapIcon: optional `replace` (e.g. leave hex battle without stacking history). */
  startFogTransition: (
    path: string,
    state?: unknown,
    navigateOpts?: Pick<NavigateOptions, 'replace'>,
  ) => void
  clearFog: () => void
  /**
   * When opening `/world-map` without a prior `startFogTransition` (e.g. diplomacy link, deep link),
   * snap the fog to the fully-covered state so {@link WorldMapPage} can reveal with `clearFog` like MapIcon.
   */
  ensureWorldMapFogCover: () => void
  phase: FogPhase
  /**
   * Blob URLs for the 2 fog sprites extracted from the cliff atlas. Empty array until extraction
   * completes. Re-used by the world-map ambient cloud drift overlay so we don't double-decode.
   */
  fogSpriteUrls: string[]
}

const FogTransitionContext = createContext<FogTransitionCtx>({
  startFogTransition: () => {},
  clearFog: () => {},
  ensureWorldMapFogCover: () => {},
  phase: 'hidden',
  fogSpriteUrls: [],
})

export function useFogTransition() {
  return useContext(FogTransitionContext)
}

/* ═══════════════════════════════════════════════════════════════════
   Atlas helpers — extract fog-1 / fog-2 from the cliff spritesheet
   ═══════════════════════════════════════════════════════════════════ */

const PUBLIC_URL = process.env.PUBLIC_URL || ''
const ATLAS_PNG = `${PUBLIC_URL}/assets/maps/world-map-cliff.png`

const FOG_FRAME_DEFS = [
  { x: 0, y: 279, w: 414, h: 180, rotated: true, sourceW: 414, sourceH: 180 },
  { x: 180, y: 279, w: 414, h: 171, rotated: true, sourceW: 414, sourceH: 180 },
] as const
/** Same rects as world-map-cliff.json fog-2.png / fog-1.png — pair with `CLIFF_FOG_SPRITE_KEYS` in terrial-engine/constants.ts. */

/**
 * Slice fog frames out of the cliff atlas without blocking the main thread on PNG Base64.
 * `toDataURL` sync-encodes ~150KB+ strings and forces immediate decode when reused by 18 `<img>` tags — major jank.
 */
function extractFogSpritesAsync(atlas: HTMLImageElement): Promise<string[]> {
  return Promise.all(
    FOG_FRAME_DEFS.map(
      (f) =>
        new Promise<string>((resolve) => {
          const c = document.createElement('canvas')
          c.width = f.sourceW
          c.height = f.sourceH
          const ctx = c.getContext('2d', { alpha: true })
          if (!ctx) {
            resolve('')
            return
          }
          if (f.rotated) {
            ctx.translate(c.width / 2, c.height / 2)
            ctx.rotate(-Math.PI / 2)
            ctx.drawImage(
              atlas,
              f.x,
              f.y,
              f.h,
              f.w,
              -c.height / 2,
              -c.width / 2,
              c.height,
              c.width,
            )
          } else {
            ctx.drawImage(atlas, f.x, f.y, f.w, f.h, 0, 0, c.width, c.height)
          }
          /**
           * WebP encodes ~3-5x faster than PNG and decodes faster in the consumer `<img>` tags too.
           * Fog is decorative; lossy 0.9 is visually indistinguishable from PNG here.
           */
          c.toBlob(
            (blob) => {
              if (!blob) {
                resolve('')
                return
              }
              resolve(URL.createObjectURL(blob))
            },
            'image/webp',
            0.9,
          )
        }),
    ),
  )
}

/* ═══════════════════════════════════════════════════════════════════
   Pre-computed cloud layout (deterministic, built once)
   ═══════════════════════════════════════════════════════════════════ */

/** Cloud motion duration — slightly snappier but still readable */
const CLOSE_MS = 560
const OPEN_MS = 420
const MAX_STAGGER = 80
const TOTAL_CLOSE_MS = CLOSE_MS + MAX_STAGGER
const TOTAL_OPEN_MS = OPEN_MS + MAX_STAGGER
const CLOSE_WAIT = TOTAL_CLOSE_MS + 40
const OPEN_WAIT = TOTAL_OPEN_MS + 40
/** After map is ready: stay at full cloud briefly, then wipe away smoothly */
const FOG_HOLD_AFTER_LOAD_MS = 120
/** Fewer layers = less compositing during the transition. */
const PER_SIDE = 7
/**
 * Dark forest-green wash behind fog transitions (and lazy shell) — saturated green, not lime / gray.
 */
export const WORLD_MAP_FOG_BACKDROP =
  'radial-gradient(ellipse at 50% 42%, #356d2e 0%, #275522 38%, #1a4018 72%, #122a11 100%)'

/** Smooth ease-out — less mechanical than default ease */
const EASE_CLOUD = 'cubic-bezier(0.22, 1, 0.36, 1)'
const EASE_BACKDROP = 'cubic-bezier(0.33, 1, 0.68, 1)'

function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

interface CloudDef {
  side: 'left' | 'right'
  spriteIdx: number
  topPct: number
  scale: number
  opacity: number
  delayMs: number
  /** Per-cloud drift motion — each cloud gets its own direction, amplitude, and speed. */
  driftDx1: number
  driftDy1: number
  driftDx2: number
  driftDy2: number
  driftDurSec: number
}

const CLOUD_DEFS: CloudDef[] = (() => {
  const r = mulberry32(0xcafe)
  const out: CloudDef[] = []
  for (const side of ['left', 'right'] as const) {
    for (let i = 0; i < PER_SIDE; i++) {
      /**
       * Two mid-keyframe targets per cloud (a wander path through two intermediate offsets) so
       * each cloud traces its own curve rather than ping-ponging between the same two points.
       * Amplitude ~15-35px each axis — clearly visible motion without looking chaotic.
       */
      const rand = (min: number, max: number) => min + r() * (max - min)
      out.push({
        side,
        spriteIdx: i % FOG_FRAME_DEFS.length,
        topPct: (i / (PER_SIDE - 1)) * 110 - 10 + (r() - 0.5) * 12,
        scale: 0.85 + r() * 0.35,
        opacity: 0.55 + r() * 0.45,
        delayMs: Math.floor(r() * MAX_STAGGER),
        driftDx1: rand(-30, 30),
        driftDy1: rand(-18, 18),
        driftDx2: rand(-30, 30),
        driftDy2: rand(-18, 18),
        driftDurSec: rand(5.5, 9.5),
      })
    }
  }
  return out
})()

/* ═══════════════════════════════════════════════════════════════════
   Fog overlay — covers the entire viewport during transitions
   ═══════════════════════════════════════════════════════════════════ */

function FogOverlay({
  phase,
  fogUrls,
}: {
  phase: FogPhase
  fogUrls: string[]
}) {
  const [cloudsIn, setCloudsIn] = useState(false)

  useEffect(() => {
    if (phase === 'closing') {
      setCloudsIn(false)
      /** One frame delay so layout commits before cloud slide-in. */
      const id = requestAnimationFrame(() => setCloudsIn(true))
      return () => cancelAnimationFrame(id)
    }
    if (phase === 'closed') {
      /** Direct `/world-map` loads jump to `closed` from `hidden` — settle clouds without a prior `closing` tick. */
      const id = requestAnimationFrame(() => setCloudsIn(true))
      return () => cancelAnimationFrame(id)
    }
    if (phase === 'opening') setCloudsIn(false)
    if (phase === 'hidden') setCloudsIn(false)
  }, [phase])

  if (phase === 'hidden') return null

  const dur = cloudsIn ? CLOSE_MS : OPEN_MS
  /** Green field wash visible with fog — don’t tie visibility only to `cloudsIn` or closing feels empty. */
  const backdropOpaque = phase === 'closing' || phase === 'closed' || cloudsIn

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        overflow: 'hidden',
        pointerEvents: cloudsIn ? 'auto' : 'none',
        contain: 'strict',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: WORLD_MAP_FOG_BACKDROP,
          opacity: backdropOpaque ? 1 : 0,
          transition: `opacity ${dur}ms ${EASE_BACKDROP}`,
          willChange: 'opacity',
        }}
      />

      {CLOUD_DEFS.map((c, i) => {
        const src = fogUrls[c.spriteIdx]
        if (!src) return null
        const offX = c.side === 'left' ? '-110%' : '110%'
        /**
         * Three layers so the transforms compose cleanly (each layer owns one transform): the
         * outer wrapper does the entry/exit slide, the middle wrapper does the continuous drift
         * keyframe, and the inner `<img>` handles per-cloud scale + horizontal flip.
         */

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              ...(c.side === 'left' ? { left: '-5%' } : { right: '-5%' }),
              top: `${c.topPct}%`,
              width: '60vw',
              height: 'auto',
              opacity: c.opacity,
              transform: `translate3d(${cloudsIn ? '0%' : offX}, 0, 0)`,
              transition: `transform ${dur}ms ${EASE_CLOUD} ${c.delayMs}ms`,
              pointerEvents: 'none',
              willChange: 'transform',
            }}
          >
            <div
              style={{
                /**
                 * Per-cloud drift — unique keyframe `eclipsera-fog-drift-${i}` with its own
                 * direction / amplitude / duration, so every cloud wanders on its own path.
                 * Negative delay scrubs each cloud to a different phase point on startup.
                 */
                animation: `eclipsera-fog-drift-${i} ${c.driftDurSec}s ease-in-out ${-(c.delayMs * 30)}ms infinite`,
                willChange: 'transform',
              }}
            >
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  transform: `scale(${c.scale})${c.side === 'right' ? ' scaleX(-1)' : ''}`,
                  transformOrigin: 'center',
                }}
              />
            </div>
          </div>
        )
      })}
      <style>{
        CLOUD_DEFS.map(
          (c, i) => `
            @keyframes eclipsera-fog-drift-${i} {
              0%, 100% { transform: translate(0px, 0px); }
              33%      { transform: translate(${c.driftDx1.toFixed(1)}px, ${c.driftDy1.toFixed(1)}px); }
              66%      { transform: translate(${c.driftDx2.toFixed(1)}px, ${c.driftDy2.toFixed(1)}px); }
            }
          `,
        ).join('\n')
      }</style>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   Provider — orchestrates fog phases and renders the overlay
   ═══════════════════════════════════════════════════════════════════ */

export function FogTransitionProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [phase, setPhase] = useState<FogPhase>('hidden')
  const phaseRef = useRef<FogPhase>('hidden')
  const timerRef = useRef(0)
  const pendingOpenRef = useRef(false)
  const [fogUrls, setFogUrls] = useState<string[]>([])
  const fogBlobUrlsRef = useRef<string[]>([])

  phaseRef.current = phase

  useEffect(() => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'
    img.onload = () => {
      void extractFogSpritesAsync(img).then((urls) => {
        fogBlobUrlsRef.current.forEach((u) => {
          if (u) URL.revokeObjectURL(u)
        })
        fogBlobUrlsRef.current = urls.filter(Boolean)
        setFogUrls(urls)
      })
    }
    img.src = ATLAS_PNG
    return () => {
      fogBlobUrlsRef.current.forEach((u) => {
        if (u) URL.revokeObjectURL(u)
      })
      fogBlobUrlsRef.current = []
    }
  }, [])

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = 0
    }
  }, [])

  useEffect(() => clearTimer, [clearTimer])

  const scheduleOpeningAfterHold = useCallback(() => {
    clearTimer()
    timerRef.current = window.setTimeout(() => {
      setPhase('opening')
      timerRef.current = window.setTimeout(() => setPhase('hidden'), OPEN_WAIT)
    }, FOG_HOLD_AFTER_LOAD_MS)
  }, [clearTimer])

  const startFogTransition = useCallback(
    (path: string, state?: unknown, navigateOpts?: Pick<NavigateOptions, 'replace'>) => {
      clearTimer()
      pendingOpenRef.current = false
      setPhase('closing')
      /**
       * Let the fog visibly close on the current page first. Navigating in the same
       * click tick as `setPhase('closing')` can unmount heavy land UI / mount lazy
       * world-map work before React paints the fog, which looks like "URL changed,
       * UI did not". The new route is mounted behind the closed fog.
       */
      timerRef.current = window.setTimeout(() => {
        setPhase('closed')
        navigate(path, { ...navigateOpts, state })
      }, CLOSE_WAIT)
    },
    [navigate, clearTimer],
  )

  const clearFog = useCallback(() => {
    const cur = phaseRef.current
    if (cur === 'hidden' || cur === 'opening') return

    if (cur === 'closed') {
      scheduleOpeningAfterHold()
    } else if (cur === 'closing') {
      pendingOpenRef.current = true
    }
  }, [scheduleOpeningAfterHold])

  const ensureWorldMapFogCover = useCallback(() => {
    if (phaseRef.current === 'hidden') {
      setPhase('closed')
    }
  }, [])

  const value = useMemo<FogTransitionCtx>(
    () => ({ startFogTransition, clearFog, ensureWorldMapFogCover, phase, fogSpriteUrls: fogUrls }),
    [startFogTransition, clearFog, ensureWorldMapFogCover, phase, fogUrls],
  )

  return (
    <FogTransitionContext.Provider value={value}>
      {children}
      <FogOverlay phase={phase} fogUrls={fogUrls} />
    </FogTransitionContext.Provider>
  )
}
